import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ErrorBanner from './ErrorBanner';

function ChatPanel({ token, user, wells, onClose }) {
  const [activeTab, setActiveTab] = useState('global');
  const [selectedWell, setSelectedWell] = useState('');
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [linkInput, setLinkInput] = useState('');
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorSource, setErrorSource] = useState(null); // 'load' | 'send'
  const [pendingAttachment, setPendingAttachment] = useState(null);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);

  async function fetchMessages() {
    try {
      const params =
        activeTab === 'well' && selectedWell ? { wellId: selectedWell } : {};
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/messages`, {
        headers: { Authorization: 'Bearer ' + token },
        params,
      });
      setMessages(res.data);
      setError('');
      setErrorSource(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load messages');
      setErrorSource('load');
    }
  }

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  // eslint-disable-next-line
  }, [activeTab, selectedWell]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function sendMessage(payload) {
    setLoading(true);
    setError('');
    setErrorSource(null);
    try {
      const well = wells.find((w) => w.id === selectedWell);
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/messages`,
        {
          ...payload,
          wellId: activeTab === 'well' && selectedWell ? selectedWell : null,
          wellName: activeTab === 'well' && well ? well.well_name : 'Global',
        },
        { headers: { Authorization: 'Bearer ' + token } }
      );
      setText('');
      setLinkInput('');
      setShowLinkInput(false);
      setPendingAttachment(null);
      fetchMessages();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send message');
      setErrorSource('send');
    } finally {
      setLoading(false);
    }
  }

  async function handleSend(e) {
    e.preventDefault();
    if (!text.trim()) return;
    await sendMessage({ text });
  }

  async function handleLinkSend() {
    if (!linkInput.trim()) return;
    const url = linkInput.startsWith('http') ? linkInput : 'https://' + linkInput;
    await sendMessage({
      text: text || url,
      attachment: { type: 'link', url, name: url },
    });
  }

  async function handleFilePick(e) {
    const file = e.target.files[0];
    if (!file) return;

    const maxSize = 2 * 1024 * 1024; // 2MB limit
    if (file.size > maxSize) {
      setError('File too large. Please select a file under 2MB.');
      e.target.value = '';
      return;
    }

    const isImage = file.type.startsWith('image/');
    const reader = new FileReader();

    reader.onerror = () => {
      setError('Failed to read file. Please try again.');
    };

    reader.onload = () => {
      const attachment = {
        type: isImage ? 'image' : 'file',
        name: file.name,
        data: reader.result,
        mimeType: file.type,
      };
      setPendingAttachment(attachment);
      sendMessage({ text: text || '', attachment });
    };

    reader.readAsDataURL(file);
    e.target.value = '';
  }

  function retrySend() {
    if (pendingAttachment) {
      sendMessage({ text: text || '', attachment: pendingAttachment });
    } else {
      sendMessage({ text });
    }
  }

  function renderAttachment(msg) {
    if (!msg.attachment) return null;

    const att = msg.attachment;
    const type = att.type;
    const data = att.data;
    const url = att.url;
    const name = att.name || 'file';

    if (type === 'image') {
      const src = data || url;
      if (!src) return null;
      return (
        <img
          src={src}
          alt={name}
          style={{
            maxWidth: '220px',
            maxHeight: '200px',
            borderRadius: '6px',
            marginTop: msg.text ? '8px' : '0',
            display: 'block',
            objectFit: 'cover',
          }}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      );
    }

    if (type === 'link' && url) {
      return (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          style={{
            color: '#00f5ff',
            fontSize: '12px',
            display: 'block',
            marginTop: '6px',
            wordBreak: 'break-all',
            textDecoration: 'underline',
          }}
        >
          🔗 {name || url}
        </a>
      );
    }

    if (type === 'file' && data) {
      return (
        <div
          style={{
            marginTop: '6px',
            backgroundColor: 'rgba(0,245,255,0.08)',
            border: '1px solid rgba(0,245,255,0.2)',
            borderRadius: '6px',
            padding: '8px 12px',
            fontSize: '12px',
            color: '#00f5ff',
          }}
        >
          📎 {name}
          <a
            href={data}
            download={name}
            style={{ color: '#00f5ff', marginLeft: '10px', fontSize: '11px' }}
          >
            DOWNLOAD
          </a>
        </div>
      );
    }

    return null;
  }

  const cyan = '#00f5ff';
  const border = 'rgba(0,245,255,0.15)';

  return (
    <div
      style={{
        position: 'fixed',
        right: 0,
        top: 0,
        height: '100vh',
        width: '360px',
        maxWidth: '100vw',
        backgroundColor: '#020817',
        borderLeft: `1px solid ${border}`,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1000,
        fontFamily: "'Segoe UI', sans-serif",
        boxShadow: '-10px 0 40px rgba(0,245,255,0.05)',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <div
            style={{
              fontSize: '13px',
              fontWeight: '700',
              letterSpacing: '3px',
              color: cyan,
            }}
          >
            TEAM COMMS
          </div>
          <div
            style={{
              fontSize: '10px',
              color: 'rgba(0,245,255,0.4)',
              letterSpacing: '2px',
              marginTop: '2px',
            }}
          >
            FIELD COMMUNICATIONS
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            color: 'rgba(0,245,255,0.5)',
            background: 'none',
            border: `1px solid ${border}`,
            cursor: 'pointer',
            fontSize: '11px',
            padding: '4px 10px',
            borderRadius: '4px',
          }}
        >
          ✕
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${border}` }}>
        {['global', 'well'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              flex: 1,
              padding: '10px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '10px',
              letterSpacing: '2px',
              fontWeight: '700',
              color: activeTab === tab ? cyan : 'rgba(0,245,255,0.3)',
              borderBottom:
                activeTab === tab ? `2px solid ${cyan}` : '2px solid transparent',
            }}
          >
            {tab === 'global' ? '🌐 GLOBAL' : '🛢 WELL'}
          </button>
        ))}
      </div>

      {/* Well Selector */}
      {activeTab === 'well' && (
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${border}` }}>
          <select
            value={selectedWell}
            onChange={(e) => setSelectedWell(e.target.value)}
            style={{
              width: '100%',
              backgroundColor: 'rgba(0,245,255,0.05)',
              color: 'white',
              border: `1px solid ${border}`,
              borderRadius: '6px',
              padding: '8px 12px',
              fontSize: '12px',
              outline: 'none',
            }}
          >
            <option value="">Select a well...</option>
            {wells.map((w) => (
              <option key={w.id} value={w.id}>
                {w.well_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Messages */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}
      >
        {error && (
          <ErrorBanner
            message={error}
            onRetry={errorSource === 'load' ? fetchMessages : retrySend}
            retryLabel="RETRY"
          />
        )}
        {messages.length === 0 && (
          <div
            style={{
              textAlign: 'center',
              color: 'rgba(0,245,255,0.3)',
              fontSize: '12px',
              letterSpacing: '2px',
              marginTop: '40px',
            }}
          >
            NO MESSAGES YET
          </div>
        )}
        {messages.map((msg) => {
          const isMe = msg.userId === user.id || msg.userId === user.id?.toString();
          return (
            <div
              key={msg._id}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isMe ? 'flex-end' : 'flex-start',
              }}
            >
              <div
                style={{
                  fontSize: '10px',
                  color: 'rgba(0,245,255,0.4)',
                  letterSpacing: '1px',
                  marginBottom: '4px',
                }}
              >
                {isMe ? 'YOU' : msg.userName.toUpperCase()} ·{' '}
                {new Date(msg.created_at).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>
              <div
                style={{
                  backgroundColor: isMe
                    ? 'rgba(0,245,255,0.1)'
                    : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${isMe ? 'rgba(0,245,255,0.3)' : 'rgba(255,255,255,0.1)'}`,
                  borderRadius: isMe ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                  padding: '10px 14px',
                  maxWidth: '260px',
                  fontSize: '13px',
                  color: 'rgba(255,255,255,0.85)',
                  lineHeight: '1.4',
                }}
              >
                {msg.text && <span>{msg.text}</span>}
                {renderAttachment(msg)}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Link Input */}
      {showLinkInput && (
        <div
          style={{
            padding: '8px 16px',
            borderTop: `1px solid ${border}`,
            display: 'flex',
            gap: '8px',
          }}
        >
          <input
            value={linkInput}
            onChange={(e) => setLinkInput(e.target.value)}
            placeholder="Paste URL here..."
            style={{
              flex: 1,
              backgroundColor: 'rgba(0,245,255,0.05)',
              color: 'white',
              border: `1px solid ${border}`,
              borderRadius: '6px',
              padding: '8px 12px',
              fontSize: '12px',
              outline: 'none',
            }}
          />
          <button
            onClick={handleLinkSend}
            style={{
              background: 'linear-gradient(135deg, #00f5ff, #0080ff)',
              color: '#020817',
              fontWeight: '700',
              fontSize: '10px',
              padding: '8px 12px',
              borderRadius: '6px',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            SEND
          </button>
        </div>
      )}

      {/* Input */}
      <form
        onSubmit={handleSend}
        style={{ padding: '12px 16px', borderTop: `1px solid ${border}` }}
      >
        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Send message..."
            style={{
              flex: 1,
              backgroundColor: 'rgba(0,245,255,0.05)',
              color: 'white',
              border: `1px solid ${border}`,
              borderRadius: '6px',
              padding: '10px 12px',
              fontSize: '13px',
              outline: 'none',
            }}
          />
          <button
            type="submit"
            disabled={loading || !text.trim()}
            style={{
              background: 'linear-gradient(135deg, #00f5ff, #0080ff)',
              color: '#020817',
              fontWeight: '700',
              fontSize: '11px',
              padding: '10px 14px',
              borderRadius: '6px',
              border: 'none',
              cursor: 'pointer',
              letterSpacing: '1px',
            }}
          >
            SEND
          </button>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            onClick={() => fileRef.current.click()}
            style={{
              flex: 1,
              background: 'none',
              border: `1px solid ${border}`,
              color: 'rgba(0,245,255,0.5)',
              fontSize: '10px',
              letterSpacing: '1px',
              padding: '10px 6px',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            📎 FILE / PHOTO
          </button>
          <button
            type="button"
            onClick={() => setShowLinkInput(!showLinkInput)}
            style={{
              flex: 1,
              background: 'none',
              border: `1px solid ${border}`,
              color: 'rgba(0,245,255,0.5)',
              fontSize: '10px',
              letterSpacing: '1px',
              padding: '10px 6px',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            🔗 LINK
          </button>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,.pdf,.doc,.docx,.xlsx,.txt"
          onChange={handleFilePick}
          style={{ display: 'none' }}
        />
      </form>
    </div>
  );
}

export default ChatPanel;
